import React from 'react';
import { useFormikContext } from 'formik';
import { Button } from 'react-covideo-common';
import { HiPencil, HiPlus } from 'react-icons/hi';
import { UIKitCardSection } from './UIKitCardSection';
import { Flex } from 'lib/components/styles/layout';
import { ILandingPageBuilderForm, IUIKitFormData } from '../../types';
import {
  ColorBox,
  FontFamily,
  FontSize,
  Line,
  LogoContainer,
  UIKitCardTitle,
  UIKitCardWrapper,
} from './styles';
import { buildImageField } from '../modal/LandingPageBuilderModal';
import { UIKitActionItems } from './UIKitActionItems';
import { IUIKitsQueryParams } from 'lib/api/superadmin/uikits/useQueryUIKits';

interface IUIKitCardProps {
  uiKit: IUIKitFormData;
  handleUpdateUIKit: (id: number | undefined) => void;
  params: IUIKitsQueryParams;
  scrollToTop: () => void;
  disabled: boolean;
  remove: <X = any>(index: number) => X | undefined;
  unshift: <X = any>(value: X) => number;
}

export const UIKitCard = ({
  uiKit,
  handleUpdateUIKit,
  params,
  scrollToTop,
  disabled,
  remove,
  unshift,
}: IUIKitCardProps) => {
  const { setValues } = useFormikContext<ILandingPageBuilderForm>();
  const {
    id,
    title,
    thumbnail,
    bodyBackgroundColor,
    buttonColor,
    footerBackgroundColor,
    headerBackgroundColor,
    textColor,
    fontFamily,
    bodyFontSize,
    titleFontSize,
  } = uiKit;

  const colorPalette = [
    bodyBackgroundColor,
    buttonColor,
    footerBackgroundColor,
    headerBackgroundColor,
    textColor,
  ];

  const handleUseUIKit = () => {
    setValues(prevValues => ({
      ...prevValues,
      headerLogo: buildImageField(null, thumbnail.url),
      bodyBackgroundColor,
      buttonColor,
      footerBackgroundColor,
      headerBackgroundColor,
      textColor,
      headerTextColor: textColor,
      bodyTextColor: textColor,
      buttonTextColor: textColor,
      buttonFontFamily: fontFamily,
      headerFontFamily: fontFamily,
      additionalTextFontFamily: fontFamily,
      titleFontSize, // headerFontSize
      additionalTextColor: textColor,
      fontFamily,
      bodyFontSize,
    }));
  };

  return (
    <UIKitCardWrapper>
      <Flex
        flexDirection='row'
        justifyContent='space-between'
        alignItems='center'
      >
        <UIKitCardTitle title={title}>{title}</UIKitCardTitle>
        {id && (
          <UIKitActionItems
            id={id}
            params={params}
            scrollToTop={scrollToTop}
            remove={remove}
            unshift={unshift}
          />
        )}
      </Flex>
      <UIKitCardSection title={'logo'}>
        <LogoContainer>
          <img
            src={thumbnail.url}
            alt='uikit-thumbnail-card'
            style={{ maxWidth: '120px', maxHeight: '52px' }}
          />
        </LogoContainer>
      </UIKitCardSection>
      <UIKitCardSection title={'colors'}>
        <Flex
          flexDirection='row'
          gap='12px'
          alignItems='center'
          justifyContent='center'
        >
          {colorPalette.map((color, idx) => (
            <ColorBox key={idx} color={color} />
          ))}
        </Flex>
      </UIKitCardSection>
      <UIKitCardSection
        title={'font'}
        extendStyles={{ container: { marginTop: '8px' } }}
      >
        <Flex gap='4px'>
          <FontFamily>{fontFamily}</FontFamily>
          <FontSize>{`Body ${bodyFontSize} / Title ${titleFontSize}`}</FontSize>
        </Flex>
      </UIKitCardSection>
      <Line />
      <Flex flexDirection='row' gap='12px' alignItems='center'>
        <Button
          text='Edit'
          size='small'
          variant='secondary'
          style={{ width: '116px' }}
          icon={<HiPencil />}
          onClick={() => handleUpdateUIKit(id)}
          disabled={disabled}
        />
        <Button
          text='Use'
          size='small'
          style={{ width: '116px' }}
          icon={<HiPlus />}
          onClick={handleUseUIKit}
          disabled={disabled}
        />
      </Flex>
    </UIKitCardWrapper>
  );
};
